import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControlLabel,
    Grid,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Context } from '../../../App';
import { updateEstimatesFee } from '../../../redux/Account/ShopDetails/action';
import SuccessMessageModal from '../../Common/SuccessMessage';

/**
 * ShopEstimate Component
 *
 * This component allows shop owners to configure their estimate settings:
 * - Toggle whether to offer estimates to customers
 * - Toggle whether estimates are paid or free
 * - Set the estimate fee amount (when paid estimates are enabled)
 *
 * The component fetches existing configuration from Redux store and
 * allows the user to update these settings.
 */
const ShopEstimate = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { isOpenSideBar } = useContext(Context);

    // Get shop details from Redux store
    const shopDetailsData = useSelector((state) => state?.account?.shopDetailsData);

    // Component state
    const [estimateFeeChecked, setEstimateFeeChecked] = useState(false);
    const [paidEstimateChecked, setPaidEstimateChecked] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Initialize form handling with react-hook-form
    const {
        handleSubmit,
        control,
        setValue,
        formState: { errors },
    } = useForm();

    // Update local state when shop details are received from Redux
    useEffect(() => {
        if (shopDetailsData) {
            setEstimateFeeChecked(shopDetailsData?.allowEstimateRequest);
            setPaidEstimateChecked(shopDetailsData?.isEstimatePaid);
            setValue('estimateFees', shopDetailsData?.estimateFees);
        }
    }, [shopDetailsData, setValue]);

    /**
     * Handles form submission to update estimate settings
     * @param {Object} values - Form values from react-hook-form
     */
    const onSubmitEstimateFee = async (values) => {
        setShowLoader(true);

        // Prepare payload for API
        const payload = {
            estimateFees: values.estimateFees,
            allowEstimateRequest: estimateFeeChecked,
            isEstimatePaid: paidEstimateChecked,
        };

        // Remove unnecessary fields if estimates are not offered
        if (!estimateFeeChecked) {
            delete payload.isEstimatePaid;
            delete payload.estimateFees;
        }

        // Submit changes to server
        const result = await dispatch(updateEstimatesFee(payload));
        if (result) {
            setOpenSuccessModal(true);
            setSuccessMessage('Changes Saved');
        }
        setShowLoader(false);
    };

    /**
     * Resets form to original values
     */
    const handleCancel = () => {
        setEstimateFeeChecked(shopDetailsData?.allowEstimateRequest);
        setPaidEstimateChecked(shopDetailsData?.isEstimatePaid);
        setValue('estimateFees', shopDetailsData?.estimateFees);
    };

    return (
        <form onSubmit={handleSubmit(onSubmitEstimateFee)} style={{ marginTop: '20px' }}>
            {/* Main container */}
            <Grid container sx={{ boxShadow: 3, bgcolor: 'white' }} justifyContent="space-between" padding="24px" borderRadius="10px" marginTop="32px">
                {/* Left column - Checkboxes */}
                <Grid item xs={12} sm={3} md={3} xl={3}>
                    <Typography variant="h5">Estimates</Typography>
                    <div className="flex-col">
                        {/* Toggle for offering estimates */}
                        <FormControlLabel
                            label="Offer Estimates"
                            labelPlacement="right"
                            control={
                                <Checkbox
                                    checked={estimateFeeChecked}
                                    onChange={() => setEstimateFeeChecked(!estimateFeeChecked)}
                                    inputProps={{ 'aria-label': 'controlled' }}
                                />
                            }
                        />
                        {/* Toggle for paid estimates - disabled if estimates are not offered */}
                        <FormControlLabel
                            label="Paid Estimate"
                            labelPlacement="right"
                            disabled={!estimateFeeChecked}
                            control={
                                <Checkbox
                                    checked={paidEstimateChecked}
                                    onChange={() => setPaidEstimateChecked(!paidEstimateChecked)}
                                    inputProps={{ 'aria-label': 'controlled' }}
                                />
                            }
                        />
                    </div>
                </Grid>

                {/* Right column - Fee input and buttons */}
                <Grid item xs={12} sm={8} md={8} xl={8} direction="row" spacing={1}>
                    <div className="estimate-title-checkbox">
                        <Typography variant="body1" className="label-500">
                            Estimate Fee
                        </Typography>
                    </div>

                    {/* Fee input field - disabled unless both checkboxes are enabled */}
                    <Controller
                        name="estimateFees"
                        control={control}
                        rules={{ required: 'Estimate Fee is required' }}
                        render={({ field }) => (
                            <TextField
                                style={{ marginTop: '8px' }}
                                id="outlined-basic"
                                variant="outlined"
                                fullWidth
                                disabled={!estimateFeeChecked || !paidEstimateChecked}
                                error={!!errors.estimateFees}
                                helperText={errors.estimateFees ? errors.estimateFees.message : ''}
                                {...field}
                                InputLabelProps={{ shrink: field.value?.length > 0 }}
                            />
                        )}
                    />

                    {/* Action buttons */}
                    <Grid container justifyContent="flex-end" spacing={2} style={{ marginTop: '16px' }}>
                        <Grid item spacing={2}>
                            <Stack spacing={2} direction="row">
                                <Button className="btn-text-size-manage" variant="outlined" color="primary" onClick={handleCancel}>
                                    Cancel
                                </Button>
                                <Button className="btn-text-size-manage" variant="contained" color="primary" type="submit">
                                    Save
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Loading dialog shown during API calls */}
            <Dialog fullScreen={false} open={showLoader}>
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>

            {/* Success message modal */}
            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />
        </form>
    );
};

export default ShopEstimate;
